import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsIcon from "../Icons/notifications";
import apiClient from "../../api/apiClient";
import "./Notifications.css";

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

function isVisible(n) {
    if (!n.seen) return true;
    return Date.now() - new Date(n.created_at).getTime() < SIX_HOURS_MS;
}

const NAVIGATION_MAP = {
    "COA Processing": "/dashboard",
    // Sent to platform admins only (since 2026-09-23) — they approve it in the Admin Panel.
    "New Registration": "/AdminPanel?tab=pending",
    "lab_tests": "/LabOrders",
    "crm_campaign_open": "/CRMPanel",
    "supply_chain_listed": "/settings/company/supply-chain?tab=listed-by",
    "supply_chain_partner_joined": "/settings/company/supply-chain",
    "supply_chain_disputed": "/settings/company/supply-chain",
    "supply_chain_dispute_resolved": "/settings/company/supply-chain",
};

const TYPE_LABELS = {
    "lab_tests": "Lab Tests",
    "crm_campaign_open": "Campaign",
    "crm_conversation_reply": "You have a new reply.",
    "supply_chain_listed": "Supply chain",
    "supply_chain_partner_joined": "Supply chain",
    "supply_chain_disputed": "Supply chain dispute",
    "supply_chain_dispute_resolved": "Supply chain dispute",
};

function formatTime(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
}

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [open, setOpen] = useState(false);
    const [unseenCount, setUnseenCount] = useState(0);
    const wrapperRef = useRef(null);
    const navigate = useNavigate();

    const fetchNotifications = useCallback(async () => {
        try {
            const response = await apiClient.get("/notifications");
            setNotifications(response.data);
            setUnseenCount(response.data.filter((n) => !n.seen).length);
        } catch (err) {
            console.error("Failed to fetch notifications:", err);
        }
    }, []);

    // Initial load
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    // SSE — receive new notifications instantly without polling
    useEffect(() => {
        const tokensString = localStorage.getItem("tokens");
        let token = null;
        try {
            token = JSON.parse(tokensString)?.AccessToken;
        } catch {
            return;
        }
        if (!token) return;

        const source = new EventSource(`/notifications/stream?token=${encodeURIComponent(token)}`);

        source.onmessage = (event) => {
            try {
                const notification = JSON.parse(event.data);
                setNotifications((prev) => [notification, ...prev]);
                setUnseenCount((prev) => prev + 1);
            } catch {
                // ignore malformed events
            }
        };

        source.onerror = () => {
            // Browser auto-reconnects on network blip; close only on explicit unmount
        };

        return () => source.close();
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleToggle = async () => {
        const wasOpen = open;
        setOpen((prev) => !prev);

        if (!wasOpen) {
            try {
                const response = await apiClient.get("/notifications");
                setNotifications(response.data);
                setUnseenCount(response.data.filter((n) => !n.seen).length);
            } catch (err) {
                console.error("Failed to open notifications:", err);
            }
        }
    };

    const handleNotificationClick = async (n) => {
        if (!n.seen) {
            try {
                await apiClient.put(`/notifications/${n.id}/seen`);
                setNotifications((prev) =>
                    prev.map((item) => (item.id === n.id ? { ...item, seen: true } : item))
                );
                setUnseenCount((prev) => Math.max(0, prev - 1));
            } catch (err) {
                console.error("Failed to mark notification seen:", err);
            }
        }

        if (n.type === "crm_conversation_reply") {
            console.log("[notif] crm_conversation_reply clicked, setting sessionStorage and navigating");
            setOpen(false);
            sessionStorage.setItem("crmOpenTab", "conversations");
            navigate("/CRMPanel");
            return;
        }
        console.log("[notif] type did not match crm_conversation_reply, got:", n.type);

        const destination = NAVIGATION_MAP[n.type];
        if (destination) {
            setOpen(false);
            navigate(destination);
        }
    };

    return (
        <div className="notifications-wrapper" ref={wrapperRef}>
            <div className="notifications-icon-container" onClick={handleToggle}>
                <NotificationsIcon />
                {unseenCount > 0 && (
                    <span className="notifications-badge">
                        {unseenCount > 99 ? "99+" : unseenCount}
                    </span>
                )}
            </div>

            {open && (
                <div className="notifications-dropdown">
                    {unseenCount > 0 && (
                        <div className="notifications-header">
                            <button
                                className="notifications-mark-all-seen"
                                onClick={async () => {
                                    try {
                                        await apiClient.put("/notifications/seen-all");
                                        setUnseenCount(0);
                                        setNotifications((prev) => prev.map((n) => ({ ...n, seen: true })));
                                    } catch (err) {
                                        console.error("Failed to mark all seen:", err);
                                    }
                                }}
                            >
                                Mark all as seen
                            </button>
                        </div>
                    )}
                    {notifications.filter(isVisible).length === 0 ? (
                        <div className="notifications-empty">No notifications</div>
                    ) : (
                        notifications.filter(isVisible).map((n) => (
                            <div
                                key={n.id}
                                className={`notification-item ${n.seen ? "seen" : "unseen"}${(NAVIGATION_MAP[n.type] || n.type === "crm_conversation_reply") ? " clickable" : ""}`}
                                onClick={() => handleNotificationClick(n)}
                            >
                                <div className="notification-type">{TYPE_LABELS[n.type] || n.type}</div>
                                <div className="notification-message">{n.message}</div>
                                <div className="notification-time">{formatTime(n.created_at)}</div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default Notifications;
